import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import EventMessageGroup from './event_message_group';
import Loader from '../loader';
import useInboxChannel from '../../hooks/useInboxChannel';

// イベント一覧（メッセージ）コンポーネント
//
// TGW-1033: 既存の周期ポーリングを撤廃し、MessageInboxChannel 経由の
// WebSocket でリアルタイム更新する。
function EventMessageGroupList(props) {
  const { eventId, isCompany, companyId } = props;

  const initialData = {
    myEvents: [],
    hasUnreadMessage: {},
    unreadMessageCount: {},
    lastMessages: {},
    pagenationSettings: {
      currentPage: 0,
      maxPerPage: 0,
      isLastPage: false,
    },
    appInstallUrl: '',
  };

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(initialData);

  const loadEventMessageGroupListFromServer = useCallback(() => {
    return new Promise((resolve) => {
      const cid = typeof companyId === 'undefined' ? null : companyId;
      $.ajax({
        url: '/api/message/my_event_list',
        data: { company_id: cid },
        dataType: 'json',
        cache: false,
        success: (resp) => {
          setLoading(false);
          setData((prev) => ({
            ...prev,
            ...resp,
            unreadMessageCount: resp.unreadMessageCount || {},
            lastMessages: resp.lastMessages || {},
          }));
          resolve();
        },
        error: () => {
          resolve();
        },
      });
    });
  }, [companyId]);

  const loadMoreEventMessageGroupListFromServer = useCallback(
    (page) => {
      const cid = typeof companyId === 'undefined' ? null : companyId;
      $.ajax({
        url: '/api/message/my_event_list',
        data: {
          company_id: cid,
          page: page + 1,
        },
        dataType: 'json',
        cache: false,
        success: (resp) => {
          setLoading(false);
          setData((prev) => {
            const currentEventIds = prev.myEvents.map((e) => e.id);
            const merged = prev.myEvents.concat(
              (resp.myEvents || []).filter(
                (e) => !currentEventIds.includes(e.id),
              ),
            );
            return {
              ...prev,
              myEvents: merged,
              unreadMessageCount: {
                ...(prev.unreadMessageCount || {}),
                ...(resp.unreadMessageCount || {}),
              },
              lastMessages: {
                ...(prev.lastMessages || {}),
                ...(resp.lastMessages || {}),
              },
              pagenationSettings: resp.pagenationSettings,
            };
          });
        },
        error: () => {},
      });
    },
    [companyId],
  );

  useEffect(() => {
    loadEventMessageGroupListFromServer();
  }, [loadEventMessageGroupListFromServer]);

  // F1〜F3
  const onMessageReceived = useCallback((payload) => {
    setData((prev) => {
      const targetId = payload.target_id;
      const idx = prev.myEvents.findIndex((e) => e.id === targetId);
      if (idx < 0) return prev;
      const event = prev.myEvents[idx];
      const reordered = [
        event,
        ...prev.myEvents.slice(0, idx),
        ...prev.myEvents.slice(idx + 1),
      ];

      const prevLast = (prev.lastMessages || {})[targetId] || {};
      const nextLast = {
        ...prevLast,
        body: payload.last_message_preview,
        created_at: payload.last_message_at,
        sender_id: payload.last_sender_id,
      };

      const isSelfSend = (payload.unread_count_delta || 0) === 0;
      const prevUnread = (prev.unreadMessageCount || {})[targetId] || 0;
      const nextUnread = isSelfSend
        ? prevUnread
        : prevUnread + (payload.unread_count_delta || 1);

      return {
        ...prev,
        myEvents: reordered,
        lastMessages: { ...(prev.lastMessages || {}), [targetId]: nextLast },
        unreadMessageCount: {
          ...(prev.unreadMessageCount || {}),
          [targetId]: nextUnread,
        },
      };
    });
  }, []);

  // F4
  const onUnreadCountChanged = useCallback((payload) => {
    setData((prev) => {
      const targetId = payload.target_id;
      if (!(prev.unreadMessageCount || {})[targetId]) return prev;
      return {
        ...prev,
        unreadMessageCount: {
          ...(prev.unreadMessageCount || {}),
          [targetId]: payload.unread_count || 0,
        },
      };
    });
  }, []);

  // F6
  const onNewThread = useCallback((payload) => {
    setData((prev) => {
      const targetId = payload.target_id;
      const exists = prev.myEvents.some((e) => e.id === targetId);
      if (exists) return prev;
      const group = payload.message_group;
      if (!group) return prev;
      const isSelfSend = (payload.unread_count_delta || 0) === 0;
      return {
        ...prev,
        myEvents: [group, ...prev.myEvents],
        unreadMessageCount: {
          ...(prev.unreadMessageCount || {}),
          [targetId]: isSelfSend ? 0 : payload.unread_count_delta || 1,
        },
        lastMessages: {
          ...(prev.lastMessages || {}),
          [targetId]: {
            body: payload.last_message_preview,
            created_at: payload.last_message_at,
            sender_id: payload.last_sender_id,
          },
        },
      };
    });
  }, []);

  useInboxChannel({
    currentUserId: data.currentUserId,
    companyId: typeof companyId === 'undefined' ? null : companyId,
    scope: 'event',
    onMessageReceived,
    onUnreadCountChanged,
    onNewThread,
    refetch: loadEventMessageGroupListFromServer,
  });

  const lastEvent = data.myEvents[data.myEvents.length - 1];
  const isLastPage = data.pagenationSettings.isLastPage;

  const eventMessageGroup = data.myEvents.map((event) => {
    return (
      <EventMessageGroup
        event={event}
        currentEventId={eventId}
        hasUnreadMessage={false}
        key={event.id}
        isCompany={isCompany}
        isLast={lastEvent == event}
        unreadMessageCount={(data.unreadMessageCount || {})[event.id]}
        lastMessage={(data.lastMessages || {})[event.id]}
        companyId={companyId}
      />
    );
  });

  let eventMessageGroupList = (
    <div
      className="block-member "
      style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
    >
      <span className="txt-member my-3">参加イベントがありません</span>
    </div>
  );
  const appLinkButton = isLastPage ? (
    ''
  ) : (
    <li
      onClick={() =>
        loadMoreEventMessageGroupListFromServer(
          data.pagenationSettings.currentPage,
        )
      }
      className="hov--default"
    >
      <div className="row justify-content-around align-items-center pt-3 pb-3 border-top">
        もっとみる
      </div>
    </li>
  );
  if (data.myEvents.length !== 0) {
    eventMessageGroupList = (
      <ul className="list-unstyled mb-0">
        {eventMessageGroup}
        {appLinkButton}
      </ul>
    );
  }

  return (
    <div className="bg-white h-100 mb-2">
      <div className="container col-md-8 col-lg-6 h-100">
        <Loader isActive={loading} />
        {eventMessageGroupList}
      </div>
    </div>
  );
}

EventMessageGroupList.displayName = 'Root';
EventMessageGroupList.propTypes = {
  eventId: PropTypes.number,
  isCompany: PropTypes.bool,
  companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default EventMessageGroupList;
